import { appGetState } from 'legacy/lut/store'
import { useCallback, useMemo } from 'react'

import {
  buildAcknowledgmentStatusView,
  isCurrentUserCommitment,
} from '../lib/buildAcknowledgmentStatusView'
import { getPlanSigners, isOptionalSigner } from '../lib/getPlanSigners'
import { isReminderSentToday } from '../lib/isReminderSentToday'

import type { AcknowledgmentCommitmentItem, AcknowledgmentUpdatedPayload } from './types'
import { useAcknowledgmentActions } from './useAcknowledgmentActions'
import { useAcknowledgmentStatusData } from './useAcknowledgmentStatusData'

type UseAcknowledgmentPendingSignersParams = {
  partnerId: string
  fYear: number
  /** The offering the plan is signed for; nothing is read without one. */
  offering: string | null
  /** The plan's rows the host already holds, e.g. from the cockpit's own payload; given them, the
   *  hook reads nothing. */
  knownItems?: AcknowledgmentCommitmentItem[]
  onUpdated?: (payload: AcknowledgmentUpdatedPayload) => void | Promise<void>
}

/**
 * The signers a plan is waiting for next — the rows the server marks `SignoffPending` — in the
 * order the acknowledgment window lists them, with the two things a reader can do about each one:
 * sign it when it is their own row, or remind its signer. Also the reader's own row to sign,
 * whether or not the server marks it pending yet.
 *
 * The rows are read the same way the window reads them, so a reminder sent from here is the same
 * request, with the same notification, as the window's `Notify`.
 */
export const useAcknowledgmentPendingSigners = ({
  partnerId,
  fYear,
  offering,
  knownItems,
  onUpdated,
}: UseAcknowledgmentPendingSignersParams) => {
  const currentUserEmail = useMemo(() => appGetState().user.email ?? '', [])

  const loaded = useAcknowledgmentStatusData({
    show: Boolean(offering) && !knownItems,
    partnerId,
    fYear,
    programType: offering ?? '',
  })
  const { reload } = loaded
  const items = knownItems ?? loaded.items
  const hasLoaded = knownItems ? true : loaded.hasLoaded

  const { handleNotify, isSubmitting } = useAcknowledgmentActions({
    reload,
    partnerId,
    fYear,
    programType: offering ?? '',
    partnerInfo: null,
    onUpdated,
  })

  const signers = useMemo(
    () =>
      buildAcknowledgmentStatusView({ items }).items.filter(
        ({ SignoffPending }) => (SignoffPending ?? 0) > 0
      ),
    [items]
  )

  const isOwnRow = useCallback(
    (item: AcknowledgmentCommitmentItem) => isCurrentUserCommitment(item, currentUserEmail),
    [currentUserEmail]
  )

  /* The reader's own row the server marks `SignoffPending`: what makes the reader the plan's
     signer rather than someone to remind one. `CanSignOff` is not always recalculated, so it does
     not decide. */
  const ownRowToSign = useMemo(
    () =>
      items.find(
        (item) => (item.SignoffPending ?? 0) > 0 && isCurrentUserCommitment(item, currentUserEmail)
      ) ?? null,
    [currentUserEmail, items]
  )

  /* The required signatures the plan waits for from someone else, whatever their role: the rows
     the reader reminds, beside signing their own row if they hold one. */
  const requiredSignersToRemind = useMemo(
    () =>
      signers.filter(
        (item) =>
          !(item.IsOptional > 0) &&
          item.CanResend > 0 &&
          !isCurrentUserCommitment(item, currentUserEmail)
      ),
    [currentUserEmail, signers]
  )

  /* Every signature the plan still lacks: the required ones in signing order, then the reader's
     own optional row. */
  const planSigners = useMemo(
    () => getPlanSigners(items, currentUserEmail),
    [currentUserEmail, items]
  )

  /* The server takes one reminder per signer a day, as the window's `Notify` does. */
  const isReminded = useCallback(
    (item: AcknowledgmentCommitmentItem) => isReminderSentToday(item.EmailSentDate),
    []
  )

  return {
    signers,
    hasLoaded,
    isOwnRow,
    ownRowToSign,
    requiredSignersToRemind,
    planSigners,
    isOptionalSigner,
    isReminded,
    remind: handleNotify,
    isSubmitting,
  }
}
